import React from "react";
import "./metric-card.scss";

/**
 * The single shell every sensor panel renders into.
 *
 * Before this existed each of the seven sensors had its own markup and its own
 * stylesheet, which is why no two of them lined up. They now differ only in
 * `accent` (the metric's identity colour), their stats and their chart.
 *
 * Props:
 *   icon      node   — metric glyph, tinted with `accent`
 *   title     node   — metric name
 *   meta      node   — chips under the title (record state, timestamp, …)
 *   actions   node   — buttons in the header
 *   menu      node   — trailing control (e.g. the dashboard's card menu),
 *                      rendered after `actions` so it always sits last
 *   stats     array  — [{ label, value, unit, meta }] rendered as stat tiles
 *   accent    string — CSS colour for this metric
 *   note      node   — status description from the device, if any
 *   footer    node   — optional footer row
 *   loading   bool   — swaps the body for a skeleton
 *   empty     node   — rendered instead of children when there is no record
 */
const MetricCard = ({
  icon,
  title,
  meta,
  actions,
  menu,
  stats,
  accent,
  note,
  footer,
  loading = false,
  empty,
  children,
  className = "",
}) => (
  <section
    className={`wg-card metric-card ${className}`}
    style={accent ? { "--metric-accent": accent } : undefined}
  >
    {/* Two fixed rows: title+actions, then chips. Chips vary in width from card
        to card (a timestamp is far wider than "No records"), so keeping them
        out of the action row is what stops the buttons wrapping on some cards
        and not others. */}
    <header className="wg-card__head metric-card__head">
      <div className="metric-card__headline">
        {icon && (
          <span
            className="wg-icon-badge"
            style={{ "--badge-color": "var(--metric-accent)" }}
          >
            {icon}
          </span>
        )}
        <h3 className="wg-card__title">{title}</h3>
        {(actions || menu) && (
          <div className="wg-card__actions">
            {actions}
            {menu}
          </div>
        )}
      </div>

      <div className="wg-card__sub metric-card__meta">{meta}</div>
    </header>

    <div className="wg-card__body metric-card__body">
      {note && <p className="metric-card__note">{note}</p>}

      {stats?.length > 0 && (
        <div className="wg-stat-grid">
          {stats.map((stat) => (
            <div className="wg-stat" key={stat.label}>
              <span className="wg-stat__label">{stat.label}</span>
              <span
                className={`wg-stat__value ${stat.small ? "wg-stat__value--sm" : ""}`}
                title={typeof stat.value === "string" ? stat.value : undefined}
              >
                {stat.value}
                {stat.unit && <span className="wg-stat__unit">{stat.unit}</span>}
              </span>
              {stat.meta && <span className="wg-stat__meta">{stat.meta}</span>}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="metric-card__plot">
          <div className="wg-skeleton metric-card__skeleton" />
        </div>
      ) : empty ? (
        empty
      ) : (
        <div className="metric-card__plot">{children}</div>
      )}
    </div>

    {footer && <footer className="wg-card__footer">{footer}</footer>}
  </section>
);

export default MetricCard;
